import type { MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { AskBennyButton } from '@/components/assistant/AskBennyButton'
import { useAuth } from '@/components/auth/AuthProvider'
import { TrialChip } from '@/components/billing/TrialChip'
import {
  PortalAskBennyButton,
  usePortalAskBennyAvailable,
} from '@/components/client-portal/benny/PortalAskBennyButton'
import { ClientPortalNavModeMenu } from '@/components/client-portal/ClientPortalNavModeMenu'
import { ClientPortalNotificationCenter } from '@/components/client-portal/ClientPortalNotificationCenter'
import { RecordButton } from '@/components/desktop/RecordButton'
import { StreakChip } from '@/components/engagement/StreakChip'
import { ModeToggle } from '@/components/mode-toggle'
import { TimeEntryWidget } from '@/components/time-entry'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import { NAVIGATION_STYLE } from '@/config/rail-areas'
import { useTauriTitleBar } from '@/contexts/TauriTitleBarContext'
import { useAutoUpdater } from '@/hooks/useAutoUpdater'
import { reloadPageWithRecordingGuard } from '@/hooks/useRecordingUnloadWarning'
import { useTauriLiquidGlass } from '@/hooks/useTauriLiquidGlass'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { getShareableWebUrl } from '@/lib/utils/app-links'
import { useClientPortalStore } from '@/store/client-portal.store'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'
import { useUpdateStore } from '@/store/update.store'
import { isMacPlatform } from '@/utils/keyboard-utils'
import {
  ArrowClockwiseIcon,
  ArrowLeftIcon,
  ArrowsClockwise,
  CaretDownIcon,
  Download,
  ExportIcon,
  ShareNetworkIcon,
  SidebarSimpleIcon,
} from '@phosphor-icons/react'
import { m } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import {
  SidebarThemedChromeContext,
  TitleBarIsland,
  topBarTextClasses,
  useTitleBarTextMode,
} from './floating-top-bar'
import { FloatingNavModeMenu } from './FloatingNavModeMenu'
import { NotificationButton } from './NotificationButton'
import { OmniSearch } from './OmniSearch'
import { UserMenu } from './UserMenu'

const logger = createLogger('components/layout/TauriTitleBar')

interface TauriTitleBarProps {
  onOpenChange?: (open: boolean) => void
}

function TauriUpdateButton() {
  const { updateAvailable, dismissed, installing } = useUpdateStore()
  const { installUpdate } = useAutoUpdater({ enableListeners: false })

  if (!updateAvailable || !dismissed) {
    return null
  }

  const buttonLabel = installing ? 'Updating...' : 'Install'

  const buttonIcon = installing ? (
    <ArrowsClockwise className="h-3 w-3 animate-spin !text-white" />
  ) : (
    <Download className="h-3 w-3 !text-white" weight="bold" />
  )

  return (
    <Button
      variant="default"
      size="sm"
      data-update-pill
      onClick={() => void installUpdate()}
      disabled={installing}
      className="update-pill-force h-8 gap-1 rounded-full !border-blue-500/70 !bg-blue-600 px-3 text-[length:var(--font-size-sm)] font-medium !text-white hover:!bg-blue-500 hover:!text-white [&_svg]:!text-white"
      title="Install available update"
    >
      {buttonIcon}
      <span className="!text-white">{buttonLabel}</span>
    </Button>
  )
}

/**
 * TauriTitleBar - A Stack-like title bar for the Tauri desktop app
 *
 * Features:
 * - Fixed position at the top of the window
 * - Draggable title bar using Tauri's startDragging() API
 * - Sidebar toggle on the left
 * - Centered OmniSearch that's also draggable when not focused
 * - Timer widget, organization switcher and user menu on the right
 * - Clean, minimal design inspired by Stack and Linear
 *
 * Traffic light position is set via trafficLightPosition in tauri.conf.json
 */
export function TauriTitleBar({ onOpenChange }: TauriTitleBarProps) {
  const { toggleSidebar, state } = useSidebar()
  const setSidebarOpen = useUIPreferencesStore(s => s.setSidebarOpen)
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  // Personal spaces are detected the same way the text mode is (store, with
  // the boot-stamped attribute as the cold-start fallback).
  const { useDarkText, isPersonalTheme } = useTitleBarTextMode()
  const text = topBarTextClasses(useDarkText)
  // Floating navigation style (config/rail-areas.ts): the bar stops being a
  // solid strip and becomes part of the tinted ground the sidebar islands
  // float on, with its controls gathered into matching glass islands (admin
  // mode included — its sidebar is an island too). Personal spaces keep the
  // docked sidebar, so they keep the docked bar (AppSidebar makes the same
  // call).
  const isFloatingChrome = NAVIGATION_STYLE === 'floating' && !isPersonalTheme
  const navigate = useNavigate()
  const { setTitleBarActive } = useTauriTitleBar()
  // The search collapses to a 32px button on the right until it's in use;
  // its island sizes around whichever state it reports.
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  useTauriLiquidGlass()
  // Mark title bar as active when mounted, inactive when unmounted
  useEffect(() => {
    setTitleBarActive(true)
    return () => setTitleBarActive(false)
  }, [setTitleBarActive])

  const handleToggleSidebar = () => {
    toggleSidebar()
    setSidebarOpen(state === 'collapsed')
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    reloadPageWithRecordingGuard()
  }

  // Collapsed, the search island is one round button: a click on the rim
  // around the inner search button opens the search too.
  const handleSearchIslandClick = (event: MouseEvent<HTMLDivElement>) => {
    event.currentTarget
      .querySelector<HTMLInputElement>('[data-omni-input] input')
      ?.focus()
  }

  // On a Mac, Share opens the system share sheet (Messages, Mail, AirDrop,
  // Notes…) hung off the button, with the page's web link as the item;
  // elsewhere, and if the sheet cannot be shown, it copies the link.
  const handleShare = async (anchor?: DOMRect) => {
    const url = getShareableWebUrl()
    if (isMacPlatform() && anchor) {
      try {
        const { invoke } = await import('@tauri-apps/api/core')
        await invoke('show_share_sheet', {
          url,
          x: anchor.left,
          y: anchor.top,
          width: anchor.width,
          height: anchor.height,
        })
        return
      } catch (error) {
        logger.warn('Share sheet unavailable, copying the link instead', error)
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')
    } catch {
      toast.error('Failed to copy link')
    }
  }
  const shareLabel = isMacPlatform() ? 'Share' : 'Copy web link'
  // The Mac's own share glyph (the box with the arrow) on a Mac; the
  // three-node glyph elsewhere.
  const ShareIcon = isMacPlatform() ? ExportIcon : ShareNetworkIcon

  // Share (Copy web link) and its fold-away menu: page
  // actions rather than navigation, so on the floating bar they sit at the
  // start of the right cluster, beside Ask Benny, instead of between the
  // navigation arrows and the sidebar toggle.
  const windowActions = (
    <>
      {/* Secondary window actions: inline when the bar is wide enough
        (theme.css .titlebar-window-action-inline), otherwise folded
        behind the chevron so narrow windows stay tight. */}
      <Button
        variant="ghost"
        size="icon"
        onClick={event =>
          void handleShare(event.currentTarget.getBoundingClientRect())
        }
        className={cn(
          'titlebar-window-action-inline h-8 w-8',
          useDarkText
            ? 'text-black/50 hover:bg-black/10 hover:text-black'
            : 'text-white/50 hover:bg-white/10 hover:text-white',
        )}
        title={shareLabel}
      >
        <ShareIcon className="h-[16px] w-[16px]" weight="regular" />
        <span className="sr-only">{shareLabel}</span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'titlebar-window-actions-menu h-8 w-8',
              useDarkText
                ? 'text-black/50 hover:bg-black/10 hover:text-black data-[state=open]:bg-black/10 data-[state=open]:text-black'
                : 'text-white/50 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white',
            )}
            title="More window actions"
          >
            <CaretDownIcon className="h-[13px] w-[13px]" weight="regular" />
            <span className="sr-only">More window actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={isFloatingChrome ? 'end' : 'start'}
          className="min-w-[10rem]"
        >
          <DropdownMenuItem
            className="text-xs"
            onClick={event =>
              void handleShare(event.currentTarget.getBoundingClientRect())
            }
          >
            <ShareIcon className="mr-2 h-3.5 w-3.5" />
            {shareLabel}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )

  return (
    // Search, timer and record follow the sidebar theme on this bar.
    <SidebarThemedChromeContext value={true}>
      <div
        className={cn(
          'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
          tauriGlassEnabled && 'tauri-glass-titlebar',
          isFloatingChrome && 'floating-titlebar',
        )}
        data-tauri-drag-region
        // Style hooks shared with the app shell frame (theme.css): same ground
        // tint and island glass, lighter when native glass is showing through.
        data-nav-style={isFloatingChrome ? 'floating' : 'docked'}
        data-glass={tauriGlassEnabled ? 'true' : 'false'}
        data-nav-text={useDarkText ? 'dark' : 'light'}
      >
        {/* Title bar with drag region and controls - 44px height, the traffic lights centred (tauri.conf trafficLightPosition) */}
        {/* CSS in index.css handles webkit-app-region for drag/no-drag based on data-tauri-drag-region */}
        <div
          // titlebar-row is a size container: the window actions below show
          // inline or fold behind the chevron based on the bar's width.
          className={cn('titlebar-row flex h-[44px] w-full items-center')}
          data-tauri-drag-region
        >
          {/* Left section - traffic light spacer + navigation + sidebar toggle + timer */}
          <div
            className={cn(
              'flex h-full flex-shrink-0 items-center pl-[100px] pr-3',
              // Floating islands sit 0.75rem apart, the same gap the sidebar
              // rail islands stack with (AppSidebar).
              isFloatingChrome ? 'gap-3' : 'gap-1',
            )}
            data-tauri-drag-region
          >
            {/* Navigation island. Floating: the sidebar toggle leads, right
              after the traffic lights where macOS keeps it, then back and
              refresh; the window actions move over to the right cluster. */}
            <TitleBarIsland floating={isFloatingChrome} index={0}>
              {isFloatingChrome && (
                <>
                  <FloatingNavModeMenu
                    className={cn('h-8 w-8', text.iconButton)}
                  />
                  <div
                    className={cn(
                      'mx-0.5 h-5 w-px',
                      useDarkText ? 'bg-black/10' : 'bg-white/10',
                    )}
                  />
                </>
              )}
              {/* Back navigation button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className={cn(
                  'h-8 w-8',
                  useDarkText
                    ? 'text-black/50 hover:bg-black/10 hover:text-black'
                    : 'text-white/50 hover:bg-white/10 hover:text-white',
                )}
                title="Go back"
              >
                <ArrowLeftIcon className="h-[16px] w-[16px]" weight="regular" />
                <span className="sr-only">Go back</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                className={cn(
                  'h-8 w-8',
                  useDarkText
                    ? 'text-black/50 hover:bg-black/10 hover:text-black'
                    : 'text-white/50 hover:bg-white/10 hover:text-white',
                )}
                title="Refresh"
              >
                <ArrowClockwiseIcon
                  className="h-[16px] w-[16px]"
                  weight="regular"
                />
                <span className="sr-only">Refresh</span>
              </Button>
              {/* Docked bar: the window actions ride along here. */}
              {!isFloatingChrome && windowActions}
            </TitleBarIsland>

            {/* Sidebar toggle — docked styles only; the floating toggle sits
              in the window island above. */}
            {!isFloatingChrome && (
              <>
                <div
                  className={cn(
                    'mx-1 h-5 w-px',
                    useDarkText ? 'bg-black/10' : 'bg-white/10',
                  )}
                />

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleToggleSidebar}
                  className={cn(
                    'h-8 w-8',
                    useDarkText
                      ? 'text-black/70 hover:bg-black/10 hover:text-black'
                      : 'text-white/70 hover:bg-white/10 hover:text-white',
                  )}
                >
                  <SidebarSimpleIcon
                    className="h-[18px] w-[18px]"
                    weight="regular"
                  />
                  <span className="sr-only">Toggle Sidebar</span>
                </Button>

                <div
                  className={cn(
                    'mx-1 h-5 w-px',
                    useDarkText ? 'bg-black/10' : 'bg-white/10',
                  )}
                />
              </>
            )}

            {/* Timer + Record island */}
            <TitleBarIsland
              floating={isFloatingChrome}
              index={1}
              className="floating-titlebar-tools"
              // The island widens when a timer starts and narrows when it
              // stops; the wrappers carry `layout` too so Record glides over
              // instead of jumping.
              layout={isFloatingChrome}
            >
              {/* Timer Widget - dynamic text colors based on theme */}
              <m.div
                layout={isFloatingChrome}
                className={cn('flex items-center', text.timer)}
              >
                <TimeEntryWidget />
              </m.div>

              {isFloatingChrome && (
                <m.div
                  layout
                  // Hidden by theme.css when Record renders nothing.
                  className={cn(
                    'titlebar-tools-divider mx-0.5 h-5 w-px',
                    useDarkText ? 'bg-black/10' : 'bg-white/10',
                  )}
                />
              )}

              {/* Meeting record widget - same dynamic text treatment */}
              <m.div
                layout={isFloatingChrome}
                className={cn('flex items-center', text.record)}
              >
                <RecordButton />
              </m.div>
            </TitleBarIsland>
          </div>

          {/* Center section - OmniSearch + Benny */}
          <div
            className={cn(
              'flex min-w-0 flex-1 items-center justify-end',
              // Floating: the right padding is the gap to the account island,
              // so Benny, search and the account island share one 0.75rem gap.
              isFloatingChrome ? 'pl-3 pr-3' : 'px-4',
            )}
            data-tauri-drag-region
          >
            <div
              className={cn(
                'flex w-full max-w-xl items-center justify-end',
                isFloatingChrome ? 'gap-3' : 'gap-2',
              )}
              data-tauri-drag-region
            >
              <TrialChip />
              <StreakChip />
              {/* The one Benny door on desktop: popover under the pill,
                Expand hands the thread to Conversations. Shown even while
                the companion pet is running. */}
              {/* shrink-0: the expanded search takes the free width, never
                Benny's; a shrinking island let the pill spill out under it. */}
              {isFloatingChrome && (
                <TitleBarIsland floating index={2} className="shrink-0">
                  {windowActions}
                </TitleBarIsland>
              )}
              <TitleBarIsland
                floating={isFloatingChrome}
                index={3}
                chrome
                className="shrink-0"
              >
                <AskBennyButton />
              </TitleBarIsland>
              {/* Docked bar: search keeps its place beside Benny. Floating:
                it moves into the account island on the right. */}
              {!isFloatingChrome && (
                <div
                  className={cn(
                    'flex min-w-0 justify-end',
                    isSearchExpanded && 'w-full',
                  )}
                >
                  <OmniSearch
                    className="w-full"
                    collapsible
                    onExpandedChange={setIsSearchExpanded}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right section - utilities, org switcher, and user menu. Floating:
              the shell's 0.375rem gutter, so the last island lines up with
              the canvas edge below it. */}
          <div
            className={cn(
              'flex h-full flex-shrink-0 items-center gap-2',
              isFloatingChrome ? 'pr-1.5' : 'pr-4',
            )}
          >
            <TitleBarIsland
              floating={isFloatingChrome}
              index={4}
              // theme.css sizes these controls to match the other islands.
              className="floating-titlebar-account gap-1"
            >
              {/* Search leads the account island: collapsed, a 36×28
                  capsule like the bell beside it; opened, the field grows
                  inside the island. The input's own border and fill are
                  cleared in theme.css. */}
              {isFloatingChrome && (
                <div
                  className={cn(
                    'floating-titlebar-search duration-base ease-ios flex min-w-0 items-center rounded-full transition-[width]',
                    isSearchExpanded
                      ? 'w-[min(22rem,32vw)]'
                      : 'floating-titlebar-search-collapsed w-9 cursor-pointer',
                  )}
                  onClick={
                    isSearchExpanded ? undefined : handleSearchIslandClick
                  }
                >
                  <OmniSearch
                    className="w-full"
                    collapsible
                    onExpandedChange={setIsSearchExpanded}
                  />
                </div>
              )}
              {/* Wrap utility buttons with explicit text color for visibility */}
              <div
                className={cn(
                  'flex items-center gap-1',
                  useDarkText
                    ? 'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70'
                    : 'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
                )}
              >
                {/* Appearance lives in Settings → Account settings. */}
                <NotificationButton />
              </div>
              <TauriUpdateButton />

              <UserMenu
                onOpenChange={onOpenChange}
                getNavigationPath={path => path}
              />
            </TitleBarIsland>
          </div>
        </div>
      </div>
    </SidebarThemedChromeContext>
  )
}

export function TauriTitleBarBasic() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTitleBarActive } = useTauriTitleBar()
  const { useDarkText } = useTitleBarTextMode()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const { isAuthenticated } = useAuth()
  useTauriLiquidGlass()
  const isClientPortalRoute = location.pathname.startsWith('/client-portal')
  // Portal sidebar expand/collapse lives in the titlebar on desktop, matching
  // the main app's toggle placement.
  const portalSidebarCollapsed = useClientPortalStore(
    s => s.desktopSidebarCollapsed,
  )
  const setPortalSidebarCollapsed =
    useClientPortalStore.getState().setDesktopSidebarCollapsed
  const portalEntitySegment = isClientPortalRoute
    ? location.pathname.split('/').filter(Boolean)[1]
    : undefined
  const portalEntityId =
    portalEntitySegment && portalEntitySegment !== 'invite'
      ? portalEntitySegment
      : undefined
  // The pill hides itself when portal Benny is off; skip its island too, or
  // an empty pill sits in the title bar.
  const isPortalBennyAvailable = usePortalAskBennyAvailable(
    isAuthenticated ? portalEntityId : undefined,
  )

  useEffect(() => {
    setTitleBarActive(true)
    return () => setTitleBarActive(false)
  }, [setTitleBarActive])

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    reloadPageWithRecordingGuard()
  }

  // Client portal routes join the floating navigation style
  // (config/rail-areas.ts) like the main title bar: the bar takes the
  // portal's ground and its controls gather into islands. Colors follow the
  // portal branding (theme.css, "Client portal: floating navigation style").
  // Native glass is left off so the bar matches the portal layout's ground.
  const isPortalFloating =
    isClientPortalRoute && NAVIGATION_STYLE === 'floating'

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && !isPortalFloating && 'tauri-glass-titlebar',
        isPortalFloating && 'floating-titlebar',
      )}
      data-tauri-drag-region
      data-nav-style={isPortalFloating ? 'floating' : undefined}
      data-glass={isPortalFloating ? 'false' : undefined}
      data-portal-titlebar={isPortalFloating ? 'true' : undefined}
    >
      <div
        className={cn('flex h-[44px] w-full items-center')}
        data-tauri-drag-region
      >
        <div
          className={cn(
            'flex h-full flex-shrink-0 items-center pl-[100px] pr-3',
            isPortalFloating ? 'gap-3' : 'gap-1',
          )}
          data-tauri-drag-region
        >
          {/* Navigation island: the sidebar toggle leads, right after the
            traffic lights where macOS keeps it, then back and refresh,
            like the app's bar. */}
          <TitleBarIsland floating={isPortalFloating} index={0}>
            {isClientPortalRoute && (
              <>
                {isPortalFloating ? (
                  <ClientPortalNavModeMenu />
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setPortalSidebarCollapsed(!portalSidebarCollapsed)
                    }
                    className={cn(
                      'h-8 w-8',
                      useDarkText
                        ? 'text-black/70 hover:bg-black/10 hover:text-black'
                        : 'text-white/70 hover:bg-white/10 hover:text-white',
                    )}
                    title="Toggle sidebar"
                  >
                    <SidebarSimpleIcon
                      className="h-[18px] w-[18px]"
                      weight="regular"
                    />
                    <span className="sr-only">Toggle sidebar</span>
                  </Button>
                )}
                <div
                  className={cn(
                    'h-5 w-px',
                    isPortalFloating ? 'mx-0.5' : 'mx-1',
                    useDarkText ? 'bg-black/10' : 'bg-white/10',
                  )}
                />
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className={cn(
                'h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Go back"
            >
              <ArrowLeftIcon className="h-[16px] w-[16px]" weight="regular" />
              <span className="sr-only">Go back</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className={cn(
                'h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Refresh"
            >
              <ArrowClockwiseIcon
                className="h-[16px] w-[16px]"
                weight="regular"
              />
              <span className="sr-only">Refresh</span>
            </Button>
          </TitleBarIsland>
        </div>

        <div
          className="flex min-w-0 flex-1 items-center justify-center px-4"
          data-tauri-drag-region
        />

        {/* Right section - mode toggle and user menu (only when authenticated) */}
        <div
          className={cn(
            'flex h-full flex-shrink-0 items-center pr-4',
            isPortalFloating ? 'gap-3' : 'gap-2',
          )}
        >
          {/* Portal Benny: the client's own conversations, never org Benny. */}
          {isClientPortalRoute &&
          isAuthenticated &&
          portalEntityId &&
          isPortalBennyAvailable ? (
            <TitleBarIsland floating={isPortalFloating} index={1} chrome>
              <PortalAskBennyButton entityId={portalEntityId} />
            </TitleBarIsland>
          ) : null}
          <TitleBarIsland
            floating={isPortalFloating}
            index={1}
            // The app bar's account island rules (theme.css): bell at the
            // title-bar scale, the user menu a circle clear of its badge.
            className="floating-titlebar-account gap-1"
          >
            <div
              className={cn(
                'flex items-center gap-1',
                useDarkText
                  ? 'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70'
                  : 'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
              )}
            >
              <ModeToggle
                showSidebarTheme={!isClientPortalRoute}
                showGlassToggle={!isClientPortalRoute}
              />
              {isClientPortalRoute && isAuthenticated ? (
                <ClientPortalNotificationCenter entityId={portalEntityId} />
              ) : null}
            </div>
            <TauriUpdateButton />
            {/* The workspace switcher heads the portal sidebar. */}
            {isAuthenticated && (
              <UserMenu
                hideOrgSettings
                showHelpItems={false}
                getNavigationPath={path => path}
              />
            )}
          </TitleBarIsland>
        </div>
      </div>
    </div>
  )
}

/**
 * Height of the TauriTitleBar in pixels - used for offsetting content.
 * 44px = h-[44px]; the traffic lights are centred on it by
 * trafficLightPosition in the desktop app's tauri.conf, and on macOS 26+ by
 * center_traffic_lights (src-tauri/src/window_style.rs), which keeps the
 * same 22pt centre line — change both if this height changes.
 */
export const TAURI_TITLE_BAR_HEIGHT = 44

/**
 * Minimal navigation bar for popup/focus windows
 * Back and reload buttons only - thin and unobtrusive
 * No pop-out button since we're already in a popup window
 */
export function PopupNavBar() {
  const navigate = useNavigate()
  const location = useLocation()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const { useDarkText } = useTitleBarTextMode()
  useTauriLiquidGlass()
  const isSettingsWindow = location.pathname === '/desktop-settings'

  const handleBack = () => navigate(-1)
  const handleRefresh = () => reloadPageWithRecordingGuard()

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && 'tauri-glass-titlebar',
      )}
      data-tauri-drag-region
    >
      <div
        className="relative flex h-8 items-center pl-[76px]"
        data-tauri-drag-region
      >
        {isSettingsWindow && (
          <span
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-semibold',
              useDarkText ? 'text-black/65' : 'text-white/70',
            )}
            data-tauri-drag-region
          >
            Settings
          </span>
        )}
        {!isSettingsWindow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className={cn(
              'h-6 w-6',
              useDarkText
                ? 'text-black/60 hover:bg-black/10 hover:text-black'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
            title="Go back"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5" weight="regular" />
          </Button>
        )}
        {!isSettingsWindow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className={cn(
              'h-6 w-6',
              useDarkText
                ? 'text-black/60 hover:bg-black/10 hover:text-black'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
            title="Reload"
          >
            <ArrowClockwiseIcon className="h-3.5 w-3.5" weight="regular" />
          </Button>
        )}
      </div>
    </div>
  )
}

export const POPUP_NAV_BAR_HEIGHT = 32
